import React from "react";

// Sentry
import "./sentry.client";

import { hydrateRoot, createRoot } from "react-dom/client";
import App from "./app";
import { BrowserRouter } from "react-router-dom";

// Styling
import "./../scss/style.scss";

// Redux
import { Provider } from "react-redux";
import { createStore, combineReducers } from "redux";
import { appReducer } from "./app-reducer";
import { timelineReducer } from "./general-components/content-base/paragraphs/timeline/reducer-timeline";
import { calendarMonthReducer } from "./general-components/content-base/paragraphs/calendar-month/reducer-calendar-month";
import { cloudViewReducer } from "./general-components/content-base/paragraphs/content-cloud/cloud-view-reducer";
import i18nReducer from "./i18n/i18n-slice";

// GraphQL
import { restHost, graphQlEndpoint } from "./config";
import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { getCurrentLanguage } from "@default/lib/lib";
import PageTransitionProvider from "@default/js/page-transition/page-transition-provider";
import inMemoryCache from "./cache";

import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import axios from "axios";
import { AxiosProvider } from "./lib/axios-provider";
import { loadErrorMessages, loadDevMessages } from "@apollo/client/dev";

const sentryReduxEnhancer = Sentry.createReduxEnhancer();

const store = createStore(
  combineReducers({
    appStore: appReducer,
    timeLine: timelineReducer,
    calendarMonth: calendarMonthReducer,
    contentCloud: cloudViewReducer,
    i18n: i18nReducer,
  }),
  window.__PRELOADED_STATE__ || {},
  sentryReduxEnhancer
);

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) =>
      console.error("[GraphQL error]", message, locations, path)
    );
  }
  if (networkError) {
    console.error("[Network error]", networkError);
  }
});

// Adds messages only in a dev environment
if (process.env.NODE_ENV === "development") {
  loadDevMessages();
  loadErrorMessages();
}

const cache = inMemoryCache.restore(window.__APOLLO_STATE__);
/* await persistCache({
  cache,
  storage: new SessionStorageWrapper(window.sessionStorage),
}); */

const client = new ApolloClient({
  link: from([
    errorLink,
    createHttpLink({
      uri: () => {
        const currentLanguage = getCurrentLanguage(window.location.pathname);

        return `${restHost}${
          currentLanguage !== "und" && currentLanguage !== "de"
            ? "/" + currentLanguage
            : ""
        }${graphQlEndpoint}`;
      },
      credentials: "same-origin",
    }),
  ]),
  cache,
});

// REST instances (Axios + TanStack Query)
const axiosClient = axios.create({
  timeout: 10 * 1000, // 10 seconds
  headers: {
    "Content-Type": "application/json",
  },
});
axiosClient.interceptors.request.use((config) => {
  const currentLanguage = store.getState().i18n.currentLanguage;
  config.baseURL = `${restHost}${currentLanguage === "en" ? "/en" : ""}`;
  return config;
});
axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error(error);
  }
);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

history.scrollRestoration = "manual";

// Allow the passed states to be garbage-collected
delete window.__PRELOADED_STATE__;

// @todo: Produces Warning on dev build.
const container = document.getElementById("app");
if (process.env.NODE_ENV === "development") {
  let root = null;
  if (!root) {
    root = createRoot(container);
  }

  root.render(
    <AxiosProvider client={axiosClient}>
      <ApolloProvider client={client}>
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <BrowserRouter forceRefresh={false}>
              <PageTransitionProvider>
                <App />
              </PageTransitionProvider>
            </BrowserRouter>
          </Provider>
        </QueryClientProvider>
      </ApolloProvider>
    </AxiosProvider>
  );
} else {
  hydrateRoot(
    container,
    <AxiosProvider client={axiosClient}>
      <ApolloProvider client={client}>
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <BrowserRouter forceRefresh={false}>
              <PageTransitionProvider>
                <App />
              </PageTransitionProvider>
            </BrowserRouter>
          </Provider>
        </QueryClientProvider>
      </ApolloProvider>
    </AxiosProvider>
  );
}

if (module["hot"]) {
  module["hot"].accept();
}
